import 'react-big-calendar/lib/css/react-big-calendar.css'

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'

import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'

import { Row, Col, Card, Button, Spin, Typography, Divider, message, Badge } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { Calendar, Views, SlotInfo, NavigateAction, View } from 'react-big-calendar'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { useStore } from '../../stores/root-store-context'
import { ExtendedEvent, RecurrenceRule } from './types'
import { REGULAR_EVENT_TYPES, PROJECT_EVENT_COLOR, CRM_EVENT_COLOR } from './constants'
import { localizer, eventStyleGetter as baseStyleGetter, generateRecurringEvents } from './utils'
import EventComponent from './EventComponent'
import FiltersPanel from './FiltersPanel'
import AddEventModal from './AddEventModal'
import EventDetailsModal from './EventDetailsModal'
import { toLocalDate, toUtcDate } from '../../helpers/dateConverters'

dayjs.extend(utc)
const { Text } = Typography

// Add localStorage keys - now with company-specific filters
const CALENDAR_VIEW_KEY = 'calendar_view_type'
const getCompanyFilterKey = (companyId: string | number) => `calendar_filters_company_${companyId}`

const stripProjectPrefix = (id: string | number | undefined) =>
  String(id ?? '').replace(/^project_/, '')

const expandForRange = (
  base: ExtendedEvent,
  rule: RecurrenceRule | undefined,
  rangeStart: Date,
  rangeEnd: Date,
) => {
  if (!rule || rule.pattern === 'none')
    return base.end >= rangeStart && base.start <= rangeEnd ? [base] : []
  return generateRecurringEvents(base, rule).filter(
    e => e.end >= rangeStart && e.start <= rangeEnd,
  )
}

const eventStyleGetter = (ev: ExtendedEvent) => {
  const style = { ...baseStyleGetter(ev).style }
  if (ev.type === 'project') {
    style.backgroundColor = PROJECT_EVENT_COLOR
  }
  if (ev.type === 'crm') {
    style.backgroundColor = CRM_EVENT_COLOR
  }
  return { style }
}

interface Props {
  startDate: Dayjs
  endDate: Dayjs
  setStartDate: (d: Dayjs) => void
  setEndDate: (d: Dayjs) => void
}

const EnhancedCalendar: React.FC<Props> = observer(
  ({ startDate, endDate, setStartDate, setEndDate }) => {
    const { t, i18n } = useTranslation()
    const {
      employeesStore,
      applicationStore,
      eventStore,
      projectEventStore,
      projectsStore,
      crmStore,
    } = useStore()

    const currentUserId = String(applicationStore.user.id)
    // Assuming you have a company ID in your application store
    const companyId = applicationStore.user.companyId || applicationStore.company.Id

    // Load saved view type from localStorage
    const [currentView, setCurrentView] = useState<View>(() => {
      const saved = localStorage.getItem(CALENDAR_VIEW_KEY)
      return (saved as View) || Views.MONTH
    })

    const [viewRange, setViewRange] = useState({
      start: startDate.toDate(),
      end: endDate.toDate(),
    })
    const [currentDate, setCurrentDate] = useState(startDate.toDate())
    const [events, setEvents] = useState<ExtendedEvent[]>([])
    const [loading, setLoading] = useState(false)

    const [addOpen, setAddOpen] = useState(false)
    const [details, setDetails] = useState<ExtendedEvent | null>(null)
    const [eventType, setEventType] = useState<'regular' | 'project' | 'crm'>('regular')
    const [slotDate, setSlotDate] = useState<Date | null>(null)
    const [editTarget, setEditTarget] = useState<ExtendedEvent | null>(null)
    const [editMode, setEditMode] = useState<'series' | 'single' | null>(null)
    const navigatingRef = useRef(false);

    // Load saved filters from localStorage with company-specific key
    const [selectedTypes, setSelectedTypes] = useState<string[]>(() => {
      try {
        if (!companyId) return ['company', 'project', 'crm']

        const saved = localStorage.getItem(getCompanyFilterKey(companyId))
        if (saved) {
          const parsed = JSON.parse(saved)
          return parsed.selectedTypes || ['company', 'project', 'crm']
        }
      } catch (e) {
        console.error('Error loading saved filters:', e)
      }
      return ['company', 'project', 'crm']
    })

    const [selectedUsers, setSelectedUsers] = useState<string[]>(() => {
      try {
        if (!companyId) return [currentUserId]

        const saved = localStorage.getItem(getCompanyFilterKey(companyId))
        if (saved) {
          const parsed = JSON.parse(saved)
          const savedUsers = parsed.selectedUsers || []

          // Validate that saved users exist in current company
          // This will be validated after employees are loaded
          return savedUsers.length > 0 ? savedUsers : [currentUserId]
        }
      } catch (e) {
        console.error('Error loading saved user filters:', e)
      }
      return [currentUserId]
    })

    const [search, setSearch] = useState('')

    // Validate and clean up saved user selections after employees are loaded
    useEffect(() => {
      if (employeesStore.users.length > 0) {
        const validUserIds = employeesStore.users.map(u => String(u.id))

        setSelectedUsers(prev => {
          // Filter out any invalid user IDs that don't exist in current company
          const validSelections = prev.filter(id => validUserIds.includes(id))

          // If no valid selections remain, default to current user
          if (validSelections.length === 0) {
            return [currentUserId]
          }

          // Ensure current user is included if they were previously selected
          if (prev.includes(currentUserId) && !validSelections.includes(currentUserId)) {
            validSelections.push(currentUserId)
          }

          return validSelections
        })
      }
    }, [employeesStore.users, currentUserId])

    // Save filters to localStorage with company-specific key whenever they change
    useEffect(() => {
      if (!companyId) return

      const filters = {
        selectedTypes,
        selectedUsers,
        companyId, // Store company ID for validation
        savedAt: new Date().toISOString()
      }
      localStorage.setItem(getCompanyFilterKey(companyId), JSON.stringify(filters))
    }, [selectedTypes, selectedUsers, companyId])

    // Save view type to localStorage whenever it changes
    useEffect(() => {
      localStorage.setItem(CALENDAR_VIEW_KEY, currentView)
    }, [currentView])

    // Clean up old filter data from other companies (optional)
    useEffect(() => {
      if (!companyId) return

      try {
        // Get all localStorage keys
        const keys = Object.keys(localStorage)
        const filterKeyPrefix = 'calendar_filters_company_'
        const currentKey = getCompanyFilterKey(companyId)

        // Optional: Remove filters older than 30 days from other companies
        keys.forEach(key => {
          if (key.startsWith(filterKeyPrefix) && key !== currentKey) {
            try {
              const data = localStorage.getItem(key)
              if (data) {
                const parsed = JSON.parse(data)
                if (parsed.savedAt) {
                  const savedDate = new Date(parsed.savedAt)
                  const daysSince = (Date.now() - savedDate.getTime()) / (1000 * 60 * 60 * 24)
                  if (daysSince > 30) {
                    localStorage.removeItem(key)
                  }
                }
              }
            } catch {
              // If we can't parse it, it's probably old format, remove it
              localStorage.removeItem(key)
            }
          }
        })
      } catch (e) {
        console.error('Error cleaning up old filters:', e)
      }
    }, [companyId])

    useEffect(() => {
      const isDayOrWeek = currentView === Views.DAY || currentView === Views.WEEK;
      const today = dayjs();

      setViewRange({ start: startDate.toDate(), end: endDate.toDate() });

      const startMonth = dayjs(startDate).month();
      const endMonth = dayjs(endDate).month();
      const currentMonth = today.month();

      const isCurrentMonthCovered =
        (currentMonth >= startMonth && currentMonth <= endMonth) ||
        today.isBetween(startDate.startOf('month'), endDate.endOf('month'), 'day', '[]');

      if (isDayOrWeek && isCurrentMonthCovered && !navigatingRef.current) {
        setCurrentDate(today.toDate());
      } else {
        setCurrentDate(startDate.toDate());
      }
    }, [startDate, endDate, currentView]);

    useEffect(() => {
      if (navigatingRef.current) {
        const timer = setTimeout(() => {
          navigatingRef.current = false;
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [currentDate]);

    const reload = useCallback(async () => {
      setLoading(true)
      try {
        await Promise.all([employeesStore.getUsers(), projectsStore.getAll()])
        await Promise.all([
          eventStore.getAll(),
          eventStore.getBirthdays(employeesStore.users),
          projectEventStore.getAll(),
          crmStore.getCrmEvents(),
        ])
      } finally {
        setLoading(false)
      }
    }, [employeesStore, projectsStore, eventStore, projectEventStore, crmStore])

    useEffect(() => {
      void reload()
    }, [reload])

    useEffect(() => {
      const regularEv: ExtendedEvent[] = eventStore.events
        .filter(e => e.type !== 'Birthday')
        .map(e => {
          const user = employeesStore.users.find(u => u.id === e.userId)
          const start = dayjs(toLocalDate(e.startDate))
          const end = dayjs(toLocalDate(e.endDate))
          const isAllDay = start.hour() === 0 && end.hour() === 0
          return {
            id: `company_${e.id}`,
            title: `${user?.lastName ?? t('Unknown')} – ${t(e.type)}`,
            start: start.local().toDate(),
            end: isAllDay ? end.local().add(1, 'day').toDate() : end.local().toDate(),
            type: 'regular' as const,
            eventType: e.type,
            userId: String(e.userId),
            userName: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`,
            status: 'approved' as const,
          }
        })

      const birthdays: ExtendedEvent[] = []
      const startYear = dayjs(viewRange.start).year()
      const endYear = dayjs(viewRange.end).year()
      eventStore.birthdays.forEach(b => {
        const user = employeesStore.users.find(u => u.id === b.userId)
        if (!user) return
        for (let yr = startYear; yr <= endYear; yr++) {
          const date = dayjs(b.startDate).year(yr)
          if (date.isBetween(dayjs(viewRange.start), dayjs(viewRange.end), null, '[]')) {
            birthdays.push({
              id: `birthday_${user.id}_${yr}`,
              title: `${user.lastName ?? ''} – ${t('Birthday')}`,
              start: date.toDate(),
              end: date.add(1, 'day').toDate(),
              type: 'regular' as const,
              eventType: 'Birthday',
              userId: String(user.id),
              userName: `${user.firstName} ${user.lastName}`,
              status: 'approved' as const,
            })
          }
        }
      })

      const masters = projectEventStore.events.filter(e => !e.parentEventId && !e.isDeleted)
      const overrides = projectEventStore.events.filter(e => !!e.parentEventId)

      const ovMap = new Map<string, ExtendedEvent>()
      overrides.forEach(o => {
        const key = `${stripProjectPrefix(o.parentEventId ?? '')}_${dayjs(o.start).format('YYYY-MM-DD')}`
        ovMap.set(key, o)
      })

      const projEv: ExtendedEvent[] = []
      masters.forEach(master => {
        expandForRange(master, master.recurrence, viewRange.start, viewRange.end).forEach(inst => {
          const key = `${stripProjectPrefix(master.id)}_${dayjs(inst.start).format('YYYY-MM-DD')}`
          const override = ovMap.get(key)

          // Use the custom title if available, otherwise fall back to project name
          const eventTitle = master.title && master.title !== '' && !master.title.includes('Project event') && !master.title.includes('Подія проєкту')
            ? master.title
            : master.projectName || 'Project Event'

          if (override) {
            if (!override.isDeleted) {
              const overrideTitle = override.title && override.title !== '' && !override.title.includes('Project event') && !override.title.includes('Подія проєкту')
                ? override.title
                : override.projectName || eventTitle

              projEv.push({
                ...override,
                title: overrideTitle,
                userId: String(override.userId)
              })
            }
          } else {
            projEv.push({
              ...inst,
              title: eventTitle,
              userId: String(inst.userId)
            })
          }
        })
      })

      const crmEv: ExtendedEvent[] = crmStore.CrmEvents.filter(e => {
        const s = dayjs(e.dateStart).toDate()
        const en = dayjs(e.dateEnd).toDate()
        return en >= viewRange.start && s <= viewRange.end
      }).map(e => {
        const s = dayjs(toLocalDate(e.dateStart))
        const en = dayjs(toLocalDate(e.dateEnd))
        const isAllDay = s.hour() === 0 && s.minute() === 0 && en.hour() === 0 && en.minute() === 0

        return {
          id: `crm_${e.id}`,
          title: e.name,
          start: s.local().toDate(),
          end: isAllDay ? en.local().add(1, 'day').toDate() : en.local().toDate(),
          type: 'crm' as const,
          userId: '0',
          userName: '',
          status: 'approved' as const,
        }
      })

      setEvents([...regularEv, ...birthdays, ...projEv, ...crmEv])
    }, [
      employeesStore.users,
      eventStore.events,
      eventStore.birthdays,
      projectEventStore.events,
      crmStore.CrmEvents,
      t,
      viewRange,
    ])

    const onSlotSelect = (slot: SlotInfo) => {
      if (
        dayjs(slot.start).isBefore(dayjs().startOf('day')) &&
        eventType !== 'regular' &&
        eventType !== 'crm'
      ) {
        message.warning(t('CannotCreateEventsInPast'))
        return
      }
      setEventType('regular')
      setSlotDate(slot.start)
      setAddOpen(true)
    }

    const handleViewChange = (view: View) => {
      setCurrentView(view)
    }

    const pushMonthRangeToParent = (date: Date) => {
      const first = dayjs(date).startOf('month')
      const last = dayjs(date).endOf('month')
      if (!first.isSame(startDate, 'day')) setStartDate(first)
      if (!last.isSame(endDate, 'day')) setEndDate(last)
    }

    const pushDayRangeToParent = (date: Date) => {
      const first = dayjs(date).startOf('day')
      const last = dayjs(date).endOf('day')
      if (!first.isSame(startDate, 'day')) setStartDate(first)
      if (!last.isSame(endDate, 'day')) setEndDate(last)
    }

    const pushWeekRangeToParent = (date: Date) => {
      const first = dayjs(date).startOf('week')
      const last = dayjs(date).endOf('week')
      if (!first.isSame(startDate, 'day')) setStartDate(first)
      if (!last.isSame(endDate, 'day')) setEndDate(last)
    }

    const handleRangeChange = useCallback((range: any) => {
      let start: Date
      let end: Date
      if (Array.isArray(range) && range.length) {
        start = range[0]
        end = range[range.length - 1]
      } else if (range && range.start && range.end) {
        start = range.start
        end = range.end
      } else if (range instanceof Date) {
        start = dayjs(range).startOf('day').toDate()
        end = dayjs(range).endOf('day').toDate()
      } else {
        return
      }
      setViewRange({ start, end })
    }, [])

    const handleNavigate = (date: Date, view: View, _action: NavigateAction) => {
      navigatingRef.current = true;
      setCurrentDate(date)
      let start: Date
      let end: Date
      if (view === Views.MONTH) {
        start = dayjs(date).startOf('month').startOf('week').toDate()
        end = dayjs(date).endOf('month').endOf('week').toDate()
        pushMonthRangeToParent(date)
      } else if (view === Views.WEEK) {
        start = dayjs(date).startOf('week').toDate()
        end = dayjs(date).endOf('week').toDate()
        pushWeekRangeToParent(date)
      } else {
        start = dayjs(date).startOf('day').toDate()
        end = dayjs(date).endOf('day').toDate()
        pushDayRangeToParent(date)
      }
      setViewRange({ start, end })
    }

    const handleShowMore = (_: ExtendedEvent[], date: Date) => {
      const start = dayjs(date).startOf('day').toDate()
      const end = dayjs(date).endOf('day').toDate()
      setCurrentDate(start)
      setViewRange({ start, end })
      pushDayRangeToParent(date)
    }

    const visibleEvents = events.filter(ev => {
      if (ev.type === 'regular') {
        if (!selectedTypes.includes('company')) return false
        return selectedUsers.includes(ev.userId)
      }
      if (ev.type === 'crm') {
        return selectedTypes.includes('crm')
      }
      if (ev.type === 'project') {
        if (!selectedTypes.includes('project')) return false

        const isAuthorSelected = selectedUsers.includes(ev.userId)
        const hasSelectedParticipant = Array.isArray((ev as any).participants) &&
          (ev as any).participants.some((pid: any) => selectedUsers.includes(String(pid)))

        return isAuthorSelected || hasSelectedParticipant
      }
      return false
    })

    const calendarMessages = useMemo(
      () => ({
        today: t('Today'),
        previous: t('back'),
        next: t('Next'),
        month: t('Month'),
        week: t('Week'),
        day: t('Day'),
        agenda: t('Agenda'),
        date: t('Date'),
        time: t('Time'),
        event: t('Event'),
        allDay: t('AllDay'),
        showMore: (total: number) => `+${total} ${t('More')}`,
      }),
      [t],
    )

    const onSubmit = async (vals: any) => {
      setLoading(true)
      try {
        const isProject = eventType === 'project'
        const isCompany = eventType === 'regular' || eventType === 'crm'

        const startLocal = vals.dateTime
          ? dayjs(vals.dateTime)
          : isCompany
            ? dayjs(vals.date)
            : dayjs(editTarget?.start)

        const startMoment = isProject ? dayjs(toUtcDate(startLocal)) : startLocal

        if (
          startMoment.isBefore(dayjs().startOf('day')) &&
          eventType === 'project'
        ) {
          message.error(t('CannotCreateOrEditPastEvents'))
          return
        }

        const duration =
          vals.duration ??
          (editTarget ? dayjs(editTarget.end).diff(editTarget.start, 'hour', true) : 0.5)

        const endLocal =
          isCompany
            ? dayjs(vals.endDate || vals.date)
            : startLocal.clone().add(duration || 0.5, 'hour')

        const endMoment = isProject ? dayjs(toUtcDate(endLocal)) : endLocal

        if (eventType === 'regular') {
          const isConflict = eventStore.checkRegularEventForDaysConflict(
            applicationStore.user.id,
            startMoment,
            endMoment,
            vals.eventType
          )
          if (isConflict) {
            message.error(t('EventConflictDetected'));
            setLoading(false);
            return;
          }
        }

        const base: ExtendedEvent = {
          id: editTarget?.id ?? Date.now().toString(),
          title: eventType === 'project'
            ? (vals.title || projectsStore.projects.find(p => p.id === vals.projectId)?.title || t('ProjectEvent'))
            : (vals.title || t(vals.eventType)),
          start: startMoment.toDate(),
          end: endMoment.toDate(),
          type: eventType as 'regular' | 'project' | 'crm',
          userId: String(applicationStore.user.id),
          userName: `${applicationStore.user.firstName} ${applicationStore.user.lastName}`,
        }

        if (!editMode) {
          if (eventType === 'project') {
            const rule: RecurrenceRule =
              vals.recurrence ?? { pattern: 'none', interval: 1, end: { type: 'never' } }
            Object.assign(base, {
              projectId: vals.projectId,
              projectName: projectsStore.projects.find(p => p.id === vals.projectId)?.title,
              participants: vals.participants ?? [],
              startTime: dayjs(base.start).utc().format('HH:mm'),
              endTime: dayjs(base.end).utc().format('HH:mm'),
              needsApproval: vals.needsApproval ?? false,
              status: vals.needsApproval ? ('pending' as const) : ('approved' as const),
              recurrence: rule,
              description: vals.description,
            })
            await projectEventStore.addEvents([base])
          } else {
            await eventStore.add({
              startDate: startMoment.format('YYYY-MM-DD'),
              endDate: endMoment.format('YYYY-MM-DD'),
              type: vals.eventType,
              userId: base.userId,
            })
          }
        } else if (editMode === 'single' && editTarget) {
          if (eventType === 'project') {
            const masterId = stripProjectPrefix(editTarget.parentEventId || editTarget.id)
            const dto = {
              title: vals.title || base.title,
              start: base.start,
              end: base.end,
              startTime: dayjs(base.start).format('HH:mm'),
              endTime: dayjs(base.end).format('HH:mm'),
              participants: vals.participants ?? editTarget.participants,
              projectId: vals.projectId,
              projectName: projectsStore.projects.find(p => p.id === vals.projectId)?.title,
              needsApproval: vals.needsApproval ?? false,
              description: vals.description,
            }
            await projectEventStore.updateOccurrence(masterId, dto)
          }
        } else if (editMode === 'series' && editTarget) {
          if (eventType === 'project') {
            const masterId = stripProjectPrefix(editTarget.parentEventId || editTarget.id)
            const dto = {
              title: vals.title || base.title,
              startTime: dayjs(base.start).format('HH:mm'),
              endTime: dayjs(base.end).format('HH:mm'),
              participants: vals.participants ?? editTarget.participants,
              projectId: vals.projectId,
              projectName: projectsStore.projects.find(p => p.id === vals.projectId)?.title,
              recurrence: vals.recurrence,
              needsApproval: vals.needsApproval ?? false,
              description: vals.description,
            }
            await projectEventStore.updateSeries(masterId, dto)
          }
        }

        await reload()
        message.success(t('SaveSuccess'))
        setAddOpen(false)
        setSlotDate(null)
        setEditTarget(null)
        setEditMode(null)
      } finally {
        setLoading(false)
      }
    }

    return (
      <div className='calendar-font-settings'>
        <Card>
          <Row justify="space-between" align="middle">
            <Button
              style={{ marginLeft: 'auto' }}
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEventType('regular')
                setSlotDate(null)
                setEditTarget(null)
                setEditMode(null)
                setAddOpen(true)
              }}
            >
              {t('AddEvent')}
            </Button>
          </Row>

          <Row gutter={24} style={{ marginTop: 24 }}>
            <Col xs={24} lg={6}>
              <Card size="small" title={t('Filters')}>
                <FiltersPanel
                  users={employeesStore.users.map(u => ({
                    ...u,
                    id: String(u.id)
                  }))}
                  selectedTypes={selectedTypes}
                  setSelectedTypes={setSelectedTypes}
                  selectedUsers={selectedUsers}
                  setSelectedUsers={setSelectedUsers}
                  search={search}
                  setSearch={setSearch}
                  currentUserId={currentUserId}
                />
                <Divider />
                <Text strong>{t('Legend')}</Text>
                {REGULAR_EVENT_TYPES.filter(cfg => cfg.type !== 'Birthday').map(cfg => (
                  <div key={cfg.type} style={{ marginTop: 4 }}>
                    <Badge color={cfg.color} /> {cfg.icon} {t(cfg.type)}
                  </div>
                ))}
                <div style={{ marginTop: 4 }}>
                  <Badge color={PROJECT_EVENT_COLOR} /> 📋 {t('Project')}
                </div>
                <div style={{ marginTop: 4 }}>
                  <Badge color={CRM_EVENT_COLOR} /> 🤝 {t('CRM')}
                </div>
              </Card>
            </Col>

            <Col xs={24} lg={18}>
              <Spin spinning={loading}>
                <div style={{ height: 700, background: '#fff', padding: 16, borderRadius: 8 }}>
                  <Calendar
                    localizer={localizer}
                    messages={calendarMessages}
                    events={visibleEvents}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: '100%' }}
                    date={currentDate}
                    view={currentView}
                    onView={handleViewChange}
                    selectable
                    views={[Views.MONTH, Views.WEEK, Views.DAY] as any}
                    culture={i18n.language}
                    onSelectSlot={onSlotSelect}
                    onSelectEvent={e => setDetails(e as ExtendedEvent)}
                    onRangeChange={handleRangeChange}
                    onNavigate={handleNavigate}
                    onShowMore={handleShowMore}
                    components={{ event: p => <EventComponent {...p} t={t} /> }}
                    eventPropGetter={eventStyleGetter}
                    dayLayoutAlgorithm="no-overlap"
                  />
                </div>
              </Spin>
            </Col>
          </Row>
        </Card>

        <AddEventModal
          open={addOpen}
          onCancel={() => {
            setAddOpen(false)
            setSlotDate(null)
            setEditTarget(null)
            setEditMode(null)
          }}
          onSubmit={onSubmit}
          loading={loading}
          eventType={eventType}
          setEventType={setEventType}
          users={employeesStore.users}
          projects={projectsStore.projects}
          initialDate={slotDate}
          initialEvent={editTarget}
          editMode={editMode}
        />

        <EventDetailsModal
          event={details}
          onClose={() => setDetails(null)}
          onEditOne={ev => {
            setDetails(null)
            setEventType('project')
            setEditTarget(ev)
            setEditMode('single')
            setAddOpen(true)
          }}
          onEditSeries={ev => {
            setDetails(null)
            setEventType('project')
            setEditTarget(ev)
            setEditMode('series')
            setAddOpen(true)
          }}
        />
      </div>
    )
  },
)

export default EnhancedCalendar